import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserData, UserDataService } from '@app/discoverer/app-core';
import { DiscovererQueryService, IDRFilter } from '@app/discoverer/core';
import { DiscoReportContext } from '@app/discoverer/dynamic-reports';

import { HERO_FEATURES } from '../config/hero-features.data';
import {
    CHART_CONFIGURATIONS,
    TABLES_COMNFIGURATION,
} from './chart-configurations.data';
import { HOME_CONSTANTS } from '../config/home.constants';
import { HOME_ANIMATIONS } from '../config/home.animations';

import { ChartState, ChartStateService } from '../services/chart-state.service';

import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatest,
    interval,
    timer,
} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    shareReplay,
    switchMap,
    takeUntil,
} from 'rxjs/operators';
import { HeroFeature } from '../interfaces/hero-feature.interface';
import { ChartConfigVM, TableConfigVM } from '@app/shared-components/chart.interfaces';

@Component({
    selector: 'home-page',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    animations: HOME_ANIMATIONS,
})
export class HomeComponent implements OnInit, OnDestroy {
    public chartState: ChartState = {
        chartRequestIds: [],
        tableRequestIds: [],

    };

    public defaultRequests: string[] = [];

    private readonly queryServiceCache = new Map<
        string,
        DiscovererQueryService
    >();
    private readonly destroy$ = new Subject<void>();
    private readonly selectedDate$ = new BehaviorSubject<Date>(new Date());

    readonly user$: Observable<UserData> = this.userDataService.loggedInUser;

    readonly chartOptions$: Observable<any[]> = this.user$.pipe(
        map((user) => {
            const tables = CHART_CONFIGURATIONS.filter((cfg) =>
                this.hasAccessToReport(cfg, user)
            ).map((cfg) => {
                const table = {
                    ...cfg,
                };

                return table;
            })
            this.setDefaultRequests(tables, 'chart');
            return tables;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );


    readonly tableReports$: Observable<any[]> = this.user$.pipe(
        map((user) => {
            const tables = TABLES_COMNFIGURATION.filter((cfg) =>
                this.hasAccessToReport(cfg, user)
            ).map((cfg) => {
                const table = {
                    ...cfg,
                };
                return table;
            })
            this.setDefaultRequests(tables, 'table');
            return tables;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    public selectedDate: Date = new Date();
    public loading = false;
    public HOME_CONSTANTS = HOME_CONSTANTS;
    public reportContext!: DiscoReportContext;
    public heroFeatures: HeroFeature[] = HERO_FEATURES;
    public currentFeatureIndex = 0;

    readonly hexCount = 15;
    readonly hexSlots = Array.from({ length: this.hexCount });
    readonly particleCount = 12;
    readonly particleSlots = Array.from({ length: this.particleCount });
    particleTrackByIndex = (i: number) => i;
    trackByIndex = (i: number) => i;
    trackByChartIndex = (index: number, item: string) => index;
    trackByTableIndex = (index: number, item: string) => index;


    newFilters: Record<string, unknown> = { date: new Date() };

    selectedChartIds: string[] = [];
    selectedTableIds: string[] = [];

    constructor(
        private readonly userDataService: UserDataService,
        private readonly chartStateService: ChartStateService
    ) { }

    async ngOnInit(): Promise<void> {
        this.chartState = this.chartStateService.loadChartState();
        this.selectedChartIds = [...(this.chartState?.chartRequestIds ?? [])];
        this.selectedTableIds = [...(this.chartState?.tableRequestIds ?? [])];

        timer(HOME_CONSTANTS.ANIMATION_TIMINGS.FEATURE_START_DELAY)
            .pipe(
                switchMap(() =>
                    interval(HOME_CONSTANTS.ANIMATION_TIMINGS.FEATURE_INTERVAL)
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.currentFeatureIndex =
                    (this.currentFeatureIndex + 1) % this.heroFeatures.length;
            });

        this.onDateChange();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onDateChange(): void {
        const d = this.normalizeDate(this.selectedDate);
        this.selectedDate = d;
        this.newFilters = { ...this.newFilters, date: { startDate: d, endDate: d } };
        this.selectedDate$.next(d);
    }


    private hasAccessToReport(
        cfg: { appId?: string; roles?: string[]; permissions?: string[] },
        user: UserData
    ): boolean {
        const appAccess = cfg.appId ? this.hasRoleToApp(cfg.appId, user) : true;
        const permissionAccess = this.hasAllPermissions(cfg.permissions, user);
        const roleListOk =
            !cfg.roles?.length || cfg.roles.some((r) => user.roles?.has(r));
        return appAccess && roleListOk && permissionAccess;
    }

    private hasRoleToApp(appId: string, user: UserData): boolean {
        const roles = user.roles ?? new Set<string>();
        for (const role of roles) {
            if (role.startsWith(`${appId}:`)) return true;
        }
        return false;
    }

    private hasAllPermissions(
        required: string[] | undefined,
        user: UserData
    ): boolean {
        if (!required?.length) return true;
        const perms = new Set(user?.permissions ?? []);
        return required.every((p) => perms.has(p));
    }

    private normalizeDate(value: unknown): Date {
        if (value && typeof (value as any).toDate === 'function') {
            return (value as any).toDate();
        }
        const d = value instanceof Date ? value : new Date(value as any);
        return isNaN(d.getTime()) ? new Date() : d;
    }

    private setDefaultRequests(allowed: ChartConfigVM[] | TableConfigVM[], type: 'chart' | 'table'): void {
        const allowedIds = allowed.map((x) => x.requestId);
        const storedIds = type === 'chart' ? this.selectedChartIds : this.selectedTableIds;
        const validStoredIds = [...new Set(storedIds)].filter(
            (id): id is string => !!id && allowedIds.includes(id)
        );

        const countToSelect = Math.min(2, allowedIds.length);
        let finalIds: string[];
        if (validStoredIds.length > 0) {
            finalIds = validStoredIds.slice(0, countToSelect);

            if (finalIds.length < countToSelect) {
                const remainingIds = allowedIds.filter(id => !finalIds.includes(id));
                finalIds = [...finalIds, ...remainingIds.slice(0, countToSelect - finalIds.length)];
            }
        } else {
            finalIds = allowedIds.slice(0, countToSelect);
        }
        if (type === 'chart') {
            this.selectedChartIds = finalIds;
        } else {
            this.selectedTableIds = finalIds;
        }
    }

    public selected(event: string, type: 'chart' | 'table', index: number) {
        if (!this.chartState) {
            this.chartState = { chartRequestIds: [], tableRequestIds: [] };
        }

        if (type === 'chart') {
            this.selectedChartIds[index] = event;
        } else {
            this.selectedTableIds[index] = event;
        }

        this.chartStateService.saveChartState(
            this.selectedChartIds,
            this.selectedTableIds
        );
    }
}
